import Grid from '@mui/material/Grid';
import classNames from 'classnames';
import { Fragment, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import Notification from '/@/components/notification/notification';
import WarningComponent from '/@/components/notification/warning';
import colors from '/@/components/theme-provider/colors';
import settings from '/@/core/settings';
import { useCornTechnologies } from '/@/hooks/catalog';
import { _ } from '/@/locale';
import { useGetFieldMaxAndMinDatesZones } from '/@/store/fields/apiProviders';
import { getSessionCountry, getSessionLanguage } from '/@/store/session/selectors';
import { trackEvent } from '/@/store/tracking';
import { parseYearlessDate } from '/@/utils/date';
import { reverse } from '/@/utils/router';
import Title from '/@/v3/components/title';
import styles from './styles.module.css';

interface ZonesDates {
    minDate: string;
    maxDate: string;
}

const NotificationsComponent = ({
    unreadedNotifications,
    readedNotifications,
    markAllAsRead,
    handleClose,
    markNotificationAsRead,
    markNotificationAsClicked,
    seeAllView = false,
}) => {
    const [expanded, setExpanded] = useState(false);
    const [zonesDates, setZonesDates] = useState<ZonesDates>({ minDate: '', maxDate: '' });
    const language = useSelector(getSessionLanguage);
    const country = useSelector(getSessionCountry);
    const technologiesQuery = useCornTechnologies({ page: 1, pageSize: 10000 });
    const technologies = (technologiesQuery.data?.results || []).filter((t) => t.displayInCatalog);
    const history = useNavigate();
    const dispatch = useDispatch();
    const { data: zones } = useGetFieldMaxAndMinDatesZones({ country: country.code });

    useEffect(() => {
        if (zones) {
            setZonesDates(zones);
        }
    }, [zones]);

    useEffect(() => {
        const readed = Boolean(JSON.parse(localStorage.getItem('readed-warning')));
        setExpanded(!readed);
    }, []);

    const handleCloseNotification = () => {
        localStorage.setItem('readed-warning', true);
    };

    const handleSeeAll = () => {
        dispatch(trackEvent({ key: 'notifications.viewAll' }));
        history(reverse('notifications'));
        if (handleClose) {
            handleClose();
        }
    };

    const toggleVtPro4Warning = () => {
        setExpanded((prev) => {
            dispatch(trackEvent({ key: 'notifications.vtPro4', open: !prev }));
            return !prev;
        });
    };

    const showVTPRO4 =
        technologies.some((tech) => tech.name === 'VTPRO4') &&
        country.code === 'AR' &&
        parseYearlessDate.isInRange(new Date(), zonesDates.minDate, zonesDates.maxDate);

    return (
        <Fragment>
            <Grid container direction='column'>
                <Grid item sx={{ p: seeAllView ? 4 : 2 }}>
                    <Title bolder color='black'>
                        {_('Mensajes')}
                    </Title>
                </Grid>
                <Grid
                    sx={{
                        overflow: 'scroll',
                        ...(!seeAllView ? { maxHeight: 450 } : { maxHeight: { md: 'calc(100vh - 220px)' } }),
                    }}
                >
                    <Grid container item sx={{ pb: 1, px: seeAllView ? 4 : 2 }} alignItems='center'>
                        <Grid item xs={11}>
                            <Title size='md' color='black' className={styles.notificationsAmount}>
                                {!unreadedNotifications.length ? (
                                    <Title size='md' bolder theme={settings.app.theme}>
                                        {_('Estás al día! No tenés mensajes sin leer')}
                                    </Title>
                                ) : (
                                    <Fragment>
                                        {_('Tenés')}
                                        <Title
                                            size='md'
                                            bolder
                                            theme={settings.app.theme}
                                            className={styles.notificationsAmount}
                                        >
                                            <span>{unreadedNotifications.length}</span>
                                            <span className={styles.notificationsAmount}>
                                                {language === 'en' && <span>{_('sin leer')}</span>}
                                                {unreadedNotifications.length > 1 ? _('mensajes') : _('mensaje')}
                                            </span>
                                        </Title>
                                        {language !== 'en' && _('sin leer')}
                                    </Fragment>
                                )}
                            </Title>
                        </Grid>
                        {showVTPRO4 && (
                            <Grid item container justifyContent='flex-end' xs={1}>
                                <Grid item>
                                    <i
                                        className={classNames('material-icons', styles.info)}
                                        onClick={toggleVtPro4Warning}
                                    >
                                        {expanded ? 'close' : 'info'}
                                    </i>
                                </Grid>
                            </Grid>
                        )}
                    </Grid>
                    {expanded && showVTPRO4 && (
                        <WarningComponent
                            handleCloseIconNotification={() => handleCloseNotification()}
                            closePopover={handleClose}
                        />
                    )}

                    {Boolean(unreadedNotifications.length) && (
                        <Grid item container>
                            <Grid
                                item
                                container
                                sx={{ px: seeAllView ? 4 : 2, ...(expanded ? { py: 2 } : { py: 1 }) }}
                                justifyContent='space-between'
                                alignItems='center'
                            >
                                <Grid item>
                                    <Title size='md' bolder>
                                        {_('No leídos')}
                                    </Title>
                                </Grid>
                                <Grid item>
                                    <Title
                                        size='sm'
                                        bolder
                                        className={styles.button}
                                        onClick={markAllAsRead}
                                        theme={settings.app.theme}
                                    >
                                        {_('Marcar todo como leído')}
                                    </Title>
                                </Grid>
                            </Grid>

                            <Grid container>
                                {unreadedNotifications.map((notification) => {
                                    return (
                                        <Grid
                                            item
                                            xs={12}
                                            sx={{
                                                px: seeAllView ? 4 : 0,
                                                backgroundColor: '#FEFBF3',
                                                '&:hover': { backgroundColor: '#FEF4DC' },
                                            }}
                                        >
                                            <Notification
                                                closePopover={Boolean(handleClose) && handleClose}
                                                notification={notification}
                                                unreaded
                                                isNotLast={
                                                    unreadedNotifications[unreadedNotifications.length - 1] !==
                                                    notification
                                                }
                                                markAsRead={(id) => markNotificationAsRead(id)}
                                                markAsClicked={(id) => markNotificationAsClicked(id)}
                                            />
                                        </Grid>
                                    );
                                })}
                            </Grid>
                        </Grid>
                    )}

                    <Grid item container>
                        <Grid
                            item
                            container
                            sx={{ px: seeAllView ? 4 : 2, py: 2 }}
                            justifyContent='space-between'
                            alignItems='center'
                        >
                            <Grid item>
                                <Title size='md' bolder>
                                    {_('Leídos')}
                                </Title>
                            </Grid>
                            <Grid item>
                                {!seeAllView && (
                                    <Title
                                        size='sm'
                                        bolder
                                        className={styles.button}
                                        theme={settings.app.theme}
                                        onClick={handleSeeAll}
                                    >
                                        {_('Ver todos')}
                                    </Title>
                                )}
                            </Grid>
                        </Grid>
                        {Boolean(readedNotifications.length) && (
                            <Grid container>
                                {readedNotifications.map((notification) => {
                                    return (
                                        <Grid
                                            item
                                            xs={12}
                                            sx={{
                                                '&:hover': { backgroundColor: colors.gray[500] },
                                                px: seeAllView ? 4 : 3,
                                            }}
                                        >
                                            <Notification
                                                closePopover={Boolean(handleClose) && handleClose}
                                                notification={notification}
                                                unreaded={false}
                                                isNotLast={
                                                    readedNotifications[readedNotifications.length - 1] !== notification
                                                }
                                                markAsClicked={(id) => markNotificationAsClicked(id)}
                                            />
                                        </Grid>
                                    );
                                })}
                            </Grid>
                        )}
                    </Grid>
                </Grid>
            </Grid>
        </Fragment>
    );
};

export default NotificationsComponent;
